// Backend Service API calls
import axios from "axios";

let API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

// Main API client for backend services
export const apiClient = axios.create({
  baseURL: API_BASE_URL.replace(/\/$/, ""),
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Add response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access

      localStorage.removeItem("token");
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

export const emailAPI = {
  // Email Accounts and Messages API
  getEmailAccounts: (params = {}) => {
    return apiClient.get("/email/accounts", { params });
  },
  createEmailAccount: (accountData) => {
    return apiClient.post("/email/accounts", accountData);
  },
  deleteEmailAccount: (id) => {
    return apiClient.delete(`/email/accounts/${id}`);
  },
  updateEmailAccount: (id, accountData) => {
    return apiClient.put(`/email/accounts/${id}`, accountData);
  },
  testEmailAccount: (accountData) => {
    if (accountData.id) {
      return apiClient.post("/email/accounts/test-connection", null, {
        params: { account_id: accountData.id },
      });
    }
    return apiClient.post("/email/accounts/test-connection", accountData);
  },
  toggleEmailAccountActive: (id) => {
    return apiClient.post(`/email/accounts/${id}/toggle-active`);
  },

  // Emails API
  getEmailMessages: (params = {}) => {
    return apiClient.get("/email/messages", { params });
  },

  fetchEmails: (markAsSeen = true) => {
    return apiClient.get("/email/receive-emails", {
      params: { mark_as_seen: markAsSeen },
    });
  },

  deleteEmailMessage: (id) => {
    return apiClient.delete(`/email/messages/${id}`);
  },

  downloadAttachment: (attachmentId) => {
    return apiClient.get(`/email/attachments/${attachmentId}/download`, {
      responseType: "blob",
    });
  },

  // Cron Jobs API
  startEmailFetchCron: (schedule, args = {}) => {
    return apiClient.post("/email/cron/start", args, {
      params: { schedule },
    });
  },
  stopEmailFetchCron: () => {
    return apiClient.post("/email/cron/stop");
  },
  getEmailFetchCronStatus: () => {
    return apiClient.get("/email/cron/status");
  },
};

// Tasks API
export const tasksAPI = {
  getTasks: (params = {}) => {
    return apiClient.get("/tasks/", { params });
  },
  getTask: (id) => {
    return apiClient.get(`/tasks/${id}`);
  },
  createTask: (taskData) => {
    return apiClient.post("/tasks/", taskData);
  },
  updateTask: (id, taskData) => {
    return apiClient.put(`/tasks/${id}`, taskData);
  },
  deleteTask: (id) => {
    return apiClient.delete(`/tasks/${id}`);
  },
  getAssignedTasks: (params = {}) => {
    return apiClient.get("/tasks/assigned", { params });
  },
  getMentionedTasks: (params = {}) => {
    return apiClient.get("/tasks/mentioned", { params });
  },
  getCreatedTasks: (params = {}) => {
    return apiClient.get("/tasks/created", { params });
  },

  getSubtasks: (parentId) => {
    return apiClient.get(`/tasks/${parentId}/subtasks`);
  },

  getUsers: () => {
    return apiClient.get("/tasks/users");
  },

  clearArchivedTasks: () => {
    return apiClient.delete("/tasks/archived/clear");
  },
  deleteArchivedTask: (id) => {
    return apiClient.delete(`/tasks/archived/${id}`);
  },
  uploadAttachments: (id, formData) => {
    return apiClient.post(`/tasks/${id}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  deleteAttachment: (id, attachmentId) => {
    return apiClient.delete(`/tasks/${id}/attachments/${attachmentId}`);
  },
  downloadAttachment: (id, attachmentId) => {
    return apiClient.get(`/tasks/${id}/attachments/${attachmentId}`, {
      responseType: "blob",
    });
  },
};

export const usersAPI = {
  createUser: (userData) => {
    return apiClient.post("/users/", userData);
  },
  updateUser: (userId, userData) => {
    return apiClient.put(`/users/${userId}`, userData);
  },

  deleteUser: (userId) => {
    return apiClient.delete(`/users/${userId}`);
  },

  readUser: (userId) => {
    return apiClient.get(`/users/${userId}`);
  },

  listUsers: () => {
    return apiClient.get("/users/all");
  },
};

// caledar API
export const calendarAPI = {
  getCalendarEvents: (params = {}) => {
    return apiClient.get("/calendar/", { params });
  },
  getCalendarEvent: (id) => {
    return apiClient.get(`/calendar/${id}`);
  },
  createCalendarEvent: (eventData) => {
    return apiClient.post("/calendar/", eventData);
  },
  updateCalendarEvent: (id, eventData) => {
    return apiClient.put(`/calendar/${id}`, eventData);
  },
  deleteCalendarEvent: (id) => {
    return apiClient.delete(`/calendar/${id}`);
  },
};

export default apiClient;
